'use client';

import { useSession, signIn as nextAuthSignIn, signOut as nextAuthSignOut } from 'next-auth/react';
import { useEffect, useState, useRef } from 'react';
import type { User as DbUser, UserSettings } from '@/types/app/db';
import { useLogger } from '@lib/utils/logUtils';

// Global cache for user data to prevent redundant fetches
const userCache = {
  data: null as DbUser | null,
  timestamp: 0,
  loading: false,
  pendingPromise: null as Promise<DbUser | null> | null
};

// Cache expiration time (15 minutes for user data)
const CACHE_EXPIRATION = 15 * 60 * 1000;

// Debounce time to prevent rapid successive fetches (500ms)
const DEBOUNCE_TIME = 500;

/**
 * Options for the NextAuth signIn function
 */
export interface SignInOptions {
  redirect?: boolean;
  callbackUrl?: string;
}

/**
 * Sign in with a provider (default: Google)
 */
export const signIn = (provider: string = 'google', options?: SignInOptions) => {
  return nextAuthSignIn(provider, { 
    redirect: options?.redirect ?? false,
    callbackUrl: options?.callbackUrl ?? '/'
  });
};

/**
 * Auth hook return type
 */
export interface UseAuthReturn {
  session: any;
  status: 'loading' | 'authenticated' | 'unauthenticated';
  isAuthenticated: boolean;
  isLoading: boolean;
  serverVerified: boolean;
  user: DbUser | any;
  dbUser: DbUser | null;
  signIn: typeof signIn;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>; // Added method to force refresh user data
}

export interface UseAuthOptions {
  skipFetch?: boolean; // Skip fetching user data from DB
  skipServerVerification?: boolean; // Skip server verification
  lightweightFetch?: boolean; // Only verify auth status without full user data refresh
}

/**
 * Custom hook for authentication functionality
 */
export function useAuth(options?: UseAuthOptions): UseAuthReturn {
  const { data: session, status } = useSession();
  const [serverVerified, setServerVerified] = useState(false);
  const [dbUser, setDbUser] = useState<DbUser | null>(userCache.data);
  const [isLoadingDbUser, setIsLoadingDbUser] = useState(userCache.loading);
  const logger = useLogger('useAuth');
  const fetchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastEmailRef = useRef<string | null>(null);
  
  // Server-side verification for critical pages
  useEffect(() => {
    if (status !== 'authenticated' || options?.skipServerVerification) return;
    
    const verifySession = async () => {
      try {
        const res = await fetch('/api/auth/session');
        if (!res.ok) {
          // Handle authentication errors
          if (res.status === 401) {
            logger.warn('Session invalid or expired, forcing re-login');
            await signOut();
            return;
          }
        }
        
        const data = await res.json();
        
        // Check for JWT errors in the response (might be hidden in a 200 response)
        if (data.error) {
          if (data.error === 'JWT_SESSION_ERROR' || 
              (typeof data.error === 'string' && data.error.includes('decryption operation failed'))) {
            logger.warn('JWT decryption error detected, forcing re-login');
            await signOut();
            return;
          }
        }
        
        setServerVerified(data.authenticated);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        logger.error('Error verifying session:', errorMessage);
        
        // Check if the error is related to JWT decryption
        if (typeof errorMessage === 'string' && 
            (errorMessage.includes('JWT') || errorMessage.includes('decryption'))) {
          logger.warn('JWT error in session verification, forcing re-login');
          await signOut();
          return;
        }
        
        setServerVerified(false);
      }
    };
    
    verifySession();
  }, [status, options?.skipServerVerification]);

  // Lightweight verification - just check if still authenticated
  const verifyAuthentication = async (): Promise<boolean> => {
    try {
      const res = await fetch('/api/auth/verify', { method: 'HEAD' });
      
      // Handle auth verification failures
      if (!res.ok) {
        if (res.status === 401) {
          logger.warn('Auth verification failed, forcing re-login');
          await signOut();
        }
        return false;
      }
      
      return res.ok;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Error verifying authentication:', errorMessage);
      
      // Check if error is JWT related
      if (typeof errorMessage === 'string' && 
          (errorMessage.includes('JWT') || errorMessage.includes('decryption'))) {
        logger.warn('JWT error in auth verification, forcing re-login');
        await signOut();
      }
      
      return false;
    }
  };

  // Function to fetch user data from database
  const fetchDbUser = async (lightweight = false): Promise<DbUser | null> => {
    setIsLoadingDbUser(true);
    
    try {
      // Just verify auth without fetching data if lightweight option is used
      if (lightweight) {
        const isVerified = await verifyAuthentication();
        if (!isVerified) {
          throw new Error('Authentication verification failed');
        }
        // Keep using cached data if auth is still valid
        if (userCache.data) {
          setDbUser(userCache.data);
          return userCache.data;
        }
      }
      
      const res = await fetch('/api/user');
      
      if (!res.ok) {
        // Specifically handle 401 unauthorized errors
        if (res.status === 401) {
          logger.warn('User data fetch unauthorized, forcing re-login');
          await signOut();
          throw new Error('Authentication failed');
        }
        
        const errorText = await res.text();
        logger.error(`Failed to fetch user data: ${res.status} ${res.statusText}`, errorText);
        
        // Check for JWT errors in the response text
        if (errorText.includes('JWT') || errorText.includes('decryption')) {
          logger.warn('JWT error detected in user data response, forcing re-login');
          await signOut();
          throw new Error('JWT authentication failed');
        }
        
        throw new Error(`Failed to fetch user data: ${res.status} ${res.statusText}`);
      }
      
      const data = await res.json();
      
      // Check if response contains error info despite 200 status
      if (data.error) {
        if (data.error === 'JWT_SESSION_ERROR' || 
            (typeof data.error === 'string' && data.error.includes('decryption operation failed'))) {
          logger.warn('JWT decryption error in user data response, forcing re-login');
          await signOut();
          throw new Error('JWT authentication failed');
        }
      }
      
      // Update cache
      userCache.data = data.user;
      userCache.timestamp = Date.now();
      userCache.loading = false;
      userCache.pendingPromise = null;
      
      setDbUser(data.user);
      return data.user;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Error fetching user data:', errorMessage);
      
      // Clear cache on error
      userCache.data = null;
      userCache.loading = false;
      userCache.pendingPromise = null;
      
      return null;
    } finally {
      setIsLoadingDbUser(false);
    }
  };
  
  // Fetch user data from database when authenticated
  useEffect(() => {
    // Skip if explicitly requested or if not authenticated
    if (options?.skipFetch || status !== 'authenticated' || !session?.user?.email) {
      // Clear user state if not authenticated
      if (status === 'unauthenticated') {
        setDbUser(null);
      }
      return;
    }
    
    const currentEmail = session?.user?.email;
    const now = Date.now();
    
    // Skip if email hasn't changed and we already have cached data
    if (
      lastEmailRef.current === currentEmail && 
      userCache.data && 
      now - userCache.timestamp < CACHE_EXPIRATION
    ) {
      setDbUser(userCache.data);
      return;
    }
    
    // Update last email reference
    lastEmailRef.current = currentEmail;
    
    // If we already have a user and the cache is still valid, use it
    if (userCache.data && now - userCache.timestamp < CACHE_EXPIRATION) {
      setDbUser(userCache.data);
      return;
    }
    
    // If we're already loading the user data, wait for that promise
    if (userCache.loading && userCache.pendingPromise) {
      userCache.pendingPromise.then(user => {
        setDbUser(user);
      });
      return;
    }
    
    // Debounce fetch requests
    if (fetchTimerRef.current) {
      clearTimeout(fetchTimerRef.current);
    }
    
    fetchTimerRef.current = setTimeout(() => {
      // Otherwise, fetch fresh data
      userCache.loading = true;
      userCache.pendingPromise = fetchDbUser(options?.lightweightFetch);
      fetchTimerRef.current = null;
    }, DEBOUNCE_TIME);
    
    // Cleanup timer on unmount
    return () => {
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current);
      }
    };
  }, [status, session?.user?.email, options?.skipFetch, options?.lightweightFetch]);
  
  const isAuthenticated = status === 'authenticated' && !!session;
  const isLoading = status === 'loading' || isLoadingDbUser;
  
  // Combine session user with database user
  const user = dbUser || session?.user;
  
  /**
   * Force refresh user data from the database
   */
  const refreshUser = async (): Promise<void> => {
    // Only refresh if authenticated
    if (status !== 'authenticated' || !session?.user?.email) {
      return;
    }
    
    // Clear cache and fetch fresh data
    userCache.data = null;
    userCache.timestamp = 0;
    
    await fetchDbUser();
  };
  
  /**
   * Enhanced sign out that clears cookies and redirects
   */
  const signOut = async (): Promise<void> => {
    // Clear user cache
    userCache.data = null;
    userCache.timestamp = 0;
    
    // Clear cookies
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.split('=');
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
    
    // Try the standard signOut for completeness
    try {
      nextAuthSignOut({ 
        redirect: true,
        callbackUrl: '/auth/login?error=session_expired'
      });
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      logger.error("Error during signOut:", errorMessage);
      
      // Force redirect on error
      window.location.href = '/auth/login?error=session_expired';
    }
    
    return Promise.resolve();
  };
  
  return {
    session,
    status,
    isAuthenticated,
    isLoading,
    serverVerified,
    user,
    dbUser,
    signIn,
    signOut,
    refreshUser
  };
}

export { nextAuthSignOut as signOut }; 